import XLSX from 'xlsx-js-style';

import type {
  XlsxCellKey,
  XlsxParsedChart,
  XlsxParsedHyperlink,
  XlsxParsedImage,
  XlsxParsedNote,
} from './types';
import { XlsxParseLimitError } from './types';
import type { OoxmlPackageReader } from './packageReader';
import {
  firstElementBody,
  firstElementText,
  parseRelationships,
  parseXmlAttributes,
  relationshipPartPath,
  resolveZipTarget,
  stripXmlTags,
  xmlUnescape,
} from './xml';

export interface ParsedLinksAndNotes {
  hyperlinks: Map<XlsxCellKey, XlsxParsedHyperlink>;
  notes: Map<XlsxCellKey, XlsxParsedNote>;
}

function refToCellKeys(
  ref: string,
  maximum: number,
): XlsxCellKey[] {
  try {
    const range = XLSX.utils.decode_range(ref.replace(/\$/g, '').toUpperCase());
    const area = (range.e.r - range.s.r + 1) * (range.e.c - range.s.c + 1);
    if (area > maximum) {
      throw new XlsxParseLimitError(
        'materialized_cells',
        `XLSX range expands to too many cells (${area} > ${maximum}): ${ref}.`,
      );
    }
    const keys: XlsxCellKey[] = [];
    for (let row = range.s.r; row <= range.e.r; row++) {
      for (let col = range.s.c; col <= range.e.c; col++) keys.push(`${row}:${col}`);
    }
    return keys;
  } catch (error) {
    if (error instanceof XlsxParseLimitError) throw error;
    return [];
  }
}

function relationshipTarget(
  partPath: string,
  target: string,
): string | null {
  return resolveZipTarget(partPath, target);
}

export async function parseLinksAndNotes(
  reader: OoxmlPackageReader | null,
  sheetPath: string | null,
  sheetIndex: number,
): Promise<ParsedLinksAndNotes> {
  const output: ParsedLinksAndNotes = { hyperlinks: new Map(), notes: new Map() };
  if (!reader || !sheetPath) return output;
  const sheetXml = await reader.readText(sheetPath);
  if (!sheetXml) return output;
  const relsXml = await reader.readText(relationshipPartPath(sheetPath));
  const relationships = relsXml ? parseRelationships(relsXml) : new Map();

  const hyperlinkBlock = firstElementBody(sheetXml, 'hyperlinks');
  if (hyperlinkBlock != null) {
    const hyperlinkRe = /<(?:\w+:)?hyperlink\b([^>]*?)\/?\s*>/gi;
    let match: RegExpExecArray | null;
    while ((match = hyperlinkRe.exec(hyperlinkBlock)) !== null) {
      const attrs = parseXmlAttributes(match[1] ?? '');
      if (!attrs.ref) continue;
      const relationshipId = attrs['r:id'] ?? attrs['relationships:id'];
      const relationship = relationshipId ? relationships.get(relationshipId) : undefined;
      let target = relationship?.target ?? '';
      if (attrs.location) target = target ? `${target}#${attrs.location}` : attrs.location;
      if (!target) continue;
      const hyperlink: XlsxParsedHyperlink = { target };
      if (attrs.tooltip) hyperlink.tooltip = attrs.tooltip;
      for (const key of refToCellKeys(attrs.ref, reader.limits.maxMaterializedCells)) {
        output.hyperlinks.set(key, hyperlink);
      }
    }
  }

  let commentsPath: string | null = null;
  for (const relationship of relationships.values()) {
    if (!relationship.type.includes('/comments')) continue;
    commentsPath = relationshipTarget(sheetPath, relationship.target);
    break;
  }
  if (!commentsPath) {
    const fallback = `xl/comments${sheetIndex + 1}.xml`;
    if (reader.has(fallback)) commentsPath = fallback;
  }
  const commentsXml = commentsPath ? await reader.readText(commentsPath) : null;
  if (commentsXml) {
    const authors: string[] = [];
    const authorsBody = firstElementBody(commentsXml, 'authors');
    if (authorsBody != null) {
      const authorRe = /<(?:\w+:)?author\b[^>]*>([\s\S]*?)<\/(?:\w+:)?author\s*>/gi;
      let authorMatch: RegExpExecArray | null;
      while ((authorMatch = authorRe.exec(authorsBody)) !== null) {
        authors.push(xmlUnescape(stripXmlTags(authorMatch[1]).trim()));
      }
    }
    const commentRe = /<(?:\w+:)?comment\b([^>]*)>([\s\S]*?)<\/(?:\w+:)?comment\s*>/gi;
    let commentMatch: RegExpExecArray | null;
    while ((commentMatch = commentRe.exec(commentsXml)) !== null) {
      const attrs = parseXmlAttributes(commentMatch[1] ?? '');
      if (!attrs.ref) continue;
      const textBody = firstElementBody(commentMatch[2] ?? '', 'text') ?? '';
      let text = '';
      const textRe = /<(?:\w+:)?t\b[^>]*>([\s\S]*?)<\/(?:\w+:)?t\s*>/gi;
      let textMatch: RegExpExecArray | null;
      while ((textMatch = textRe.exec(textBody)) !== null) text += xmlUnescape(stripXmlTags(textMatch[1]));
      text = text.trim();
      if (!text) continue;
      const note: XlsxParsedNote = { text };
      const authorId = Number(attrs.authorId);
      if (Number.isInteger(authorId) && authors[authorId]) note.author = authors[authorId];
      const key = refToCellKeys(attrs.ref, 1)[0];
      if (key) output.notes.set(key, note);
    }
  }

  let threadedPath: string | null = null;
  for (const relationship of relationships.values()) {
    if (!relationship.type.includes('/threadedComment')) continue;
    threadedPath = relationshipTarget(sheetPath, relationship.target);
    break;
  }
  const threadedXml = threadedPath ? await reader.readText(threadedPath) : null;
  if (threadedXml) {
    const people = new Map<string, string>();
    const personsXml = await reader.readText('xl/persons/person.xml');
    if (personsXml) {
      const personRe = /<(?:\w+:)?person\b([^>]*?)\/?\s*>/gi;
      let personMatch: RegExpExecArray | null;
      while ((personMatch = personRe.exec(personsXml)) !== null) {
        const attrs = parseXmlAttributes(personMatch[1] ?? '');
        if (attrs.id && attrs.displayName) people.set(attrs.id, attrs.displayName);
      }
    }
    const threadedRe = /<(?:\w+:)?threadedComment\b([^>]*)>([\s\S]*?)<\/(?:\w+:)?threadedComment\s*>/gi;
    let threadedMatch: RegExpExecArray | null;
    const seen = new Set<XlsxCellKey>();
    while ((threadedMatch = threadedRe.exec(threadedXml)) !== null) {
      const attrs = parseXmlAttributes(threadedMatch[1] ?? '');
      if (!attrs.ref) continue;
      const key = refToCellKeys(attrs.ref, 1)[0];
      if (!key || seen.has(key)) continue;
      seen.add(key);
      const text = firstElementText(threadedMatch[2] ?? '', 'text')?.trim();
      if (!text) continue;
      const note: XlsxParsedNote = { text };
      if (attrs.personId && people.has(attrs.personId)) note.author = people.get(attrs.personId);
      output.notes.set(key, note);
    }
  }
  return output;
}

function imageMimeType(path: string): string {
  const extension = path.split('.').pop()?.toLowerCase();
  switch (extension) {
    case 'jpg':
    case 'jpeg': return 'image/jpeg';
    case 'gif': return 'image/gif';
    case 'svg': return 'image/svg+xml';
    case 'bmp': return 'image/bmp';
    case 'tif':
    case 'tiff': return 'image/tiff';
    case 'webp': return 'image/webp';
    default: return 'image/png';
  }
}

/** English Metric Units per CSS pixel at 96 DPI, the unit DrawingML anchors use. */
const EMU_PER_PIXEL = 9525;

function emuToPixels(text: string | null): number {
  const emu = Number(text ?? 0);
  if (!Number.isFinite(emu) || emu <= 0) return 0;
  return Math.round(emu / EMU_PER_PIXEL);
}

interface AnchorCoordinate {
  row: number;
  col: number;
  rowOffset: number;
  colOffset: number;
}

function anchorCoordinate(body: string, localName: 'from' | 'to'): AnchorCoordinate | null {
  const block = firstElementBody(body, localName);
  if (block == null) return null;
  const row = Number(firstElementText(block, 'row') ?? 0);
  const col = Number(firstElementText(block, 'col') ?? 0);
  if (!Number.isInteger(row) || row < 0 || !Number.isInteger(col) || col < 0) return null;
  return {
    row,
    col,
    rowOffset: emuToPixels(firstElementText(block, 'rowOff')),
    colOffset: emuToPixels(firstElementText(block, 'colOff')),
  };
}

/** Geometry every anchored drawing object shares, already converted to pixels. */
interface AnchorGeometry {
  fromRow: number;
  fromCol: number;
  fromRowOffset: number;
  fromColOffset: number;
  toRow?: number;
  toCol?: number;
  toRowOffset?: number;
  toColOffset?: number;
  width?: number;
  height?: number;
}

function anchorGeometry(body: string): AnchorGeometry | null {
  const from = anchorCoordinate(body, 'from');
  if (!from) return null;
  const geometry: AnchorGeometry = {
    fromRow: from.row,
    fromCol: from.col,
    fromRowOffset: from.rowOffset,
    fromColOffset: from.colOffset,
  };
  const to = anchorCoordinate(body, 'to');
  if (to) {
    geometry.toRow = to.row;
    geometry.toCol = to.col;
    geometry.toRowOffset = to.rowOffset;
    geometry.toColOffset = to.colOffset;
  }
  const extentTag = /<(?:\w+:)?ext\b([^>]*?)\/?\s*>/i.exec(body);
  if (extentTag) {
    const attrs = parseXmlAttributes(extentTag[1] ?? '');
    const cx = Number(attrs.cx);
    const cy = Number(attrs.cy);
    if (Number.isFinite(cx) && cx > 0) geometry.width = Math.round(cx / EMU_PER_PIXEL);
    if (Number.isFinite(cy) && cy > 0) geometry.height = Math.round(cy / EMU_PER_PIXEL);
  }
  return geometry;
}

export interface XlsxDrawingObjects {
  images: XlsxParsedImage[];
  charts: XlsxParsedChart[];
}

const EMPTY_DRAWING: XlsxDrawingObjects = { images: [], charts: [] };

/**
 * Read a sheet's drawing part into the floating objects a host can render.
 *
 * Images and charts share one pass because they live in the same anchor list and
 * re-reading the drawing part per object type would double the I/O for no gain.
 */
export async function parseDrawingObjects(
  reader: OoxmlPackageReader | null,
  sheetPath: string | null,
): Promise<XlsxDrawingObjects> {
  if (!reader || !sheetPath) return EMPTY_DRAWING;
  const relsXml = await reader.readText(relationshipPartPath(sheetPath));
  if (!relsXml) return EMPTY_DRAWING;
  const sheetRelationships = parseRelationships(relsXml);
  const drawingRelationship = [...sheetRelationships.values()].find((relationship) =>
    relationship.type.includes('/drawing') && !relationship.type.includes('/vmlDrawing'));
  if (!drawingRelationship) return EMPTY_DRAWING;
  const drawingPath = resolveZipTarget(sheetPath, drawingRelationship.target);
  if (!drawingPath) return EMPTY_DRAWING;
  const drawingXml = await reader.readText(drawingPath);
  const drawingRelsXml = await reader.readText(relationshipPartPath(drawingPath));
  if (!drawingXml || !drawingRelsXml) return EMPTY_DRAWING;
  const drawingRelationships = parseRelationships(drawingRelsXml);
  const images: XlsxParsedImage[] = [];
  const charts: XlsxParsedChart[] = [];
  const anchorRe = /<(?:\w+:)?(?:twoCellAnchor|oneCellAnchor)\b[^>]*>([\s\S]*?)<\/(?:\w+:)?(?:twoCellAnchor|oneCellAnchor)\s*>/gi;
  let anchorMatch: RegExpExecArray | null;
  while ((anchorMatch = anchorRe.exec(drawingXml)) !== null) {
    const body = anchorMatch[1] ?? '';
    const geometry = anchorGeometry(body);
    if (!geometry) continue;

    const blipTag = /<(?:\w+:)?blip\b([^>]*?)\/?\s*>/i.exec(body);
    if (blipTag) {
      const blipAttrs = parseXmlAttributes(blipTag[1] ?? '');
      const relationshipId = blipAttrs['r:embed'] ?? blipAttrs.embed;
      if (!relationshipId) continue;
      const imageRelationship = drawingRelationships.get(relationshipId);
      if (!imageRelationship || !imageRelationship.type.includes('/image')) continue;
      const imagePath = resolveZipTarget(drawingPath, imageRelationship.target);
      if (!imagePath) continue;
      const base64 = await reader.readBase64(imagePath);
      if (base64 == null) continue;
      images.push({
        ...geometry,
        mimeType: imageMimeType(imagePath),
        base64,
        sourcePath: imagePath,
      });
      continue;
    }

    const chartTag = /<(?:\w+:)?chart\b([^>]*?)\/?\s*>/i.exec(body);
    if (!chartTag) continue;
    const chartAttrs = parseXmlAttributes(chartTag[1] ?? '');
    const chartRelationshipId = chartAttrs['r:id'] ?? chartAttrs.id;
    if (!chartRelationshipId) continue;
    const chartRelationship = drawingRelationships.get(chartRelationshipId);
    if (!chartRelationship || !chartRelationship.type.includes('/chart')) continue;
    const chartPath = resolveZipTarget(drawingPath, chartRelationship.target);
    if (!chartPath) continue;
    const chartXml = await reader.readText(chartPath);
    if (!chartXml) continue;
    charts.push({ ...geometry, chartXml, sourcePath: chartPath });
  }
  return { images, charts };
}
